// ============================================================
// redirect-bridge-guard.js
// Decides whether THIS document is MSAL's hidden-iframe (or popup)
// redirect landing, so the bootstrap can hand the auth response back
// to the main window instead of booting the SPA (2026-09-11).
//
// WHY THIS EXISTS. @azure/msal-browser v5 changed how a silent token
// renewal completes. The main window opens a hidden iframe to
// /authorize?prompt=none; Entra answers with a redirect to our
// redirectUri carrying #code=…&state=…; and the main window then waits
// on a BroadcastChannel for the DOCUMENT LOADED IN THAT IFRAME to post
// the response back (`broadcastResponseToMainFrame` from
// `@azure/msal-browser/redirect-bridge`). Nothing in the library calls
// it for you, and msal-react does not either. Until 2026-09-11 neither
// SPA called it, so every hidden-iframe renew timed out after 10s
// (`redirect_bridge_timeout`) — which the platform experienced as the
// "Unable to load your profile" / "Something went wrong" screen with
// ZERO API calls, every morning a tab had been idle past its 24-hour
// refresh-token lifetime, since the first v5 build on 2026-05-10.
//
// The landing test is deliberately narrow — BOTH halves must hold:
//   1. this document is framed or popup-opened (window.parent is not
//      window, or window.opener is set); AND
//   2. the URL carries an OAuth response: `code=` or `error=` together
//      with `state=`, in the hash or the query string.
// A TOP-level redirect return (acquireTokenRedirect / loginRedirect
// landing on /login/org#code=… or /#code=…) has no parent, so it takes
// the normal handleRedirectPromise path. The Teams tab IS framed but
// never carries a code, so it boots normally. A page opened with
// window.open (the CareVoice survey preview) has an opener but no
// code, so it boots normally too.
//
// The broadcast function is INJECTED rather than imported here so this
// module stays free of the `@azure/msal-browser/redirect-bridge`
// subpath import — react-scripts 5 runs Jest 27, which does not
// resolve package `exports` subpaths, so a direct import would make
// this module untestable. index.js owns the import.
// ============================================================

/**
 * True when `win` is a framed or popup document whose URL carries an
 * OAuth authorization response (code or error, with state).
 *
 * @param {Window} win
 * @returns {boolean}
 */
export function isRedirectBridgeLanding(win) {
  if (!win || !win.location) return false;
  if (!isFramedOrPopup(win)) return false;
  return carriesAuthResponse(win.location.hash) || carriesAuthResponse(win.location.search);
}

/**
 * True when the document has a parent frame or an opener. A throw on
 * parent access (a cross-origin parent in an odd host) means there IS
 * a parent, so it counts as framed.
 */
function isFramedOrPopup(win) {
  try {
    return !!((win.parent && win.parent !== win) || win.opener);
  } catch {
    return true;
  }
}

/**
 * True when a hash or query fragment looks like an OAuth response:
 * `code=` or `error=` present together with `state=`.
 *
 * @param {string} fragment  `#...` or `?...` (leading char optional)
 */
export function carriesAuthResponse(fragment) {
  if (typeof fragment !== 'string' || fragment.length < 2) return false;
  const params = new URLSearchParams(fragment.replace(/^[#?]/, ''));
  const hasState = params.has('state') && params.get('state') !== '';
  const hasResponse = params.has('code') || params.has('error');
  return hasState && hasResponse;
}

/**
 * If this document is the redirect-bridge landing, post the response to
 * the main window via the injected broadcast function and resolve true
 * (the caller then MUST NOT boot the SPA or initialise MSAL — see
 * BrowserUtils.blockReloadInHiddenIframes). Resolves false when this is
 * an ordinary document. A broadcast failure is reported to the console
 * and still resolves true: there is nothing else this document can
 * usefully do, and the main window's own timeout handles the failure.
 *
 * @param {Window} win
 * @param {() => Promise<void>} broadcast  `broadcastResponseToMainFrame`
 * @returns {Promise<boolean>}
 */
export async function runRedirectBridgeIfLanding(win, broadcast) {
  if (!isRedirectBridgeLanding(win)) return false;
  try {
    await broadcast();
  } catch (err) {
    console.error('[auth] redirect bridge failed to post the response to the main window', err);
  }
  return true;
}
